/**
 * `window.MetaShipGlobalState` — copied verbatim (same keys, same method
 * names/shapes) from `root/index.html:1664-1780` per Appendix A5, plus a
 * `tenant` key. ~60 Vue3 module files read `window.MetaShipGlobalState.state.<key>`
 * as a plain property (not only through `getState()`), so `.state` MUST stay a
 * directly-readable object that is REPLACED (not mutated) on every update —
 * exactly the legacy `this.state = { ...this.state, ...partial }` pattern.
 *
 * This shell ALSO mirrors the same fields into `localStorage["vuex"]`
 * (Appendix A5) — the persisted-Vuex shape legacy-client's modules still read
 * directly (e.g. `modules/bookings/src/services/api.ts`).
 */
import type { NavBarItem, UserOrganisation } from "@/tenant/types";
import type { TenantState } from "@tenant-theme";

export interface HostUser {
  id: number;
  email: string;
  firstname: string;
  surname: string;
  emailAddress: string;
  avatar?: string | null;
  auth0Id?: string;
  tagId?: number | null;
  roles?: string[];
}

export interface HostState {
  darkMode: boolean;
  locale: string;
  token: string | null;
  tokenExpiry: number | null;
  orgToken: string | null;
  organisations: UserOrganisation[] | null;
  permissions: NavBarItem[] | null;
  currentOrg: UserOrganisation | null;
  currentRoute: string | null;
  user: HostUser | null;
  tenant: TenantState | null;
}

export interface MetaShipGlobalStateApi {
  state: HostState;
  subscribers: Array<(state: HostState) => void>;
  getState(): HostState;
  setState(newState: HostState): void;
  updateState(partial: Partial<HostState>): void;
  subscribe(callback: (state: HostState) => void): () => void;
  notifySubscribers(): void;
  toggleTheme(): void;
}

declare global {
  interface Window {
    MetaShipGlobalState?: MetaShipGlobalStateApi;
  }
}

const GLOBAL_STATE_KEY = "metaship-global-state";
const VUEX_MIRROR_KEY = "vuex";

export function defaultState(): HostState {
  return {
    darkMode: true,
    locale: "en",
    token: null,
    tokenExpiry: null,
    orgToken: null,
    organisations: null,
    permissions: null,
    currentOrg: null,
    currentRoute: null,
    user: null,
    tenant: null,
  };
}

/** The subset of `HostState` legacy modules read out of `localStorage["vuex"]`. */
export function toVuexMirror(state: HostState): Record<string, unknown> {
  return {
    token: state.token,
    orgToken: state.orgToken,
    user: state.user,
    currentOrg: state.currentOrg,
    darkMode: state.darkMode,
    organisations: state.organisations,
  };
}

function loadPersistedState(): HostState | null {
  try {
    const raw = localStorage.getItem(GLOBAL_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HostState>;
    return { ...defaultState(), ...parsed };
  } catch {
    return null;
  }
}

function persist(state: HostState): void {
  try {
    localStorage.setItem(GLOBAL_STATE_KEY, JSON.stringify(state));
    localStorage.setItem(VUEX_MIRROR_KEY, JSON.stringify(toVuexMirror(state)));
  } catch {
    // best-effort, mirrors legacy's swallow-and-log-nothing-in-prod behaviour
  }
}

/** Build the `MetaShipGlobalState` singleton. Exported (not just installed) for tests. */
export function createGlobalState(): MetaShipGlobalStateApi {
  const api: MetaShipGlobalStateApi = {
    state: loadPersistedState() || defaultState(),
    subscribers: [],
    getState() {
      return api.state;
    },
    setState(newState) {
      api.state = { ...newState };
      persist(api.state);
      api.notifySubscribers();
    },
    updateState(partial) {
      api.state = { ...api.state, ...partial };
      persist(api.state);
      api.notifySubscribers();
    },
    subscribe(callback) {
      api.subscribers.push(callback);
      return () => {
        const i = api.subscribers.indexOf(callback);
        if (i > -1) api.subscribers.splice(i, 1);
      };
    },
    notifySubscribers() {
      api.subscribers.forEach((cb) => cb(api.state));
    },
    toggleTheme() {
      api.updateState({ darkMode: !api.state.darkMode });
      document.body.setAttribute("data-theme", api.state.darkMode ? "dark" : "light");
    },
  };
  return api;
}

/** Install the singleton on `window` and apply the initial theme attribute. */
export function installGlobalState(): MetaShipGlobalStateApi {
  const api = createGlobalState();
  window.MetaShipGlobalState = api;
  document.body.setAttribute("data-theme", api.state.darkMode ? "dark" : "light");
  return api;
}

/** True once anything has been persisted — i.e. this is not the browser's first visit. */
export function hasPersistedState(): boolean {
  try {
    return localStorage.getItem(GLOBAL_STATE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * First visit takes the TENANT's default theme (DEV-924: Boer is light by
 * default; the shell used to start every app dark and only the Vuetify side
 * followed `branding.defaultTheme`, so the hosted module and the chrome
 * disagreed). A saved preference — the toggle in the sidebar footer — wins
 * on every later visit. Returns the theme now in force.
 */
export function applyTenantDefaultTheme(
  api: MetaShipGlobalStateApi,
  defaultTheme: "light" | "dark" | null | undefined,
  fresh: boolean = !hasPersistedState(),
): "light" | "dark" {
  if (fresh) {
    const darkMode = defaultTheme !== "light";
    if (darkMode !== api.state.darkMode) api.updateState({ darkMode });
  }
  const theme = api.state.darkMode ? "dark" : "light";
  document.body.setAttribute("data-theme", theme);
  return theme;
}
